import * as React from "react";
import { HotelsProps } from "./typings";
import { inject, observer } from "mobx-react";
import { HotelCard } from "./components/HotelCard";
import { SeeAllContentLink } from "./components/SeeAllContentLink";
import { Hotel, HotelsFlexModuleResult } from "typings/microserviceModels/hotels-flex-module";
import { UitkCardAlohaContentSection } from "uitk-react-cards";
import { CardContentReviewsPriceProposition } from "components/flexComponents/Hotels/components/CardContentReviewsPriceProposition";
import { SeeAllButton } from "components/flexComponents/Hotels/components/SeeAllButton";
import { LegalText } from "components/flexComponents/Hotels/components/LegalText";
import { Viewport, ViewSmall, ViewMedium } from "@shared-ui/viewport-context";
import { ExtendedContextStore } from "src/typings/flexFramework/FlexDefinitions";
import { HotelInterruption } from "./components/HotelInterruption/HotelInterruption";
import { isVariantEnabled, Experiment } from "src/components/utility/experiment";
import { HotelPoolAffinityFilters } from "./components/HotelAffinityFilters/HotelPoolAffinityFilters";
import { LoyaltySignup } from "./components/LoyaltySignup/LoyaltySignup";
import { LodgingRebound } from "@shared-ui/customer-lodging-rebound";
import { HotelsContext } from "components/flexComponents/Hotels/components/HotelsContext";
import { HotelStarRatingAffinityFilters } from "components/flexComponents/Hotels/components/HotelAffinityFilters/HotelStarRatingAffinityFilters";
import { SchemaOrgScript } from "components/utility/SchemaOrg/SchemaOrgScript";
import { hotelStructuredData } from "components/flexComponents/Hotels/utils/getHotelStructuredData";
import { UitkSubheading } from "uitk-react-text";
import { MessagingCard } from "./components/MessagingCard/MessagingCard";
import { PlaceholderResults } from "./components/PlaceholderResults/PlaceholderResults";
import { PriceTrendGraphAnchor } from "./components/PriceTrendGraphAnchor/PriceTrendGraphAnchor";
import { LazyLoad } from "src/components/utility/LazyLoad/LazyLoad";
import { useLocalization } from "@shared-ui/localization-context";
import { liveAnnounce } from "uitk-react-utils";

/**
 * This component is the main overview of a property and gives basic information
 * to users in the hopes of getting them to click and purchase nights in it.
 *
 * This also handles mobile and desktop views.
 */
const HotelsComponent = inject(
  "hotels",
  "rateCalendar",
  "flexViewModel",
  "flexModuleModelStore",
  "context"
)(
  observer((props: HotelsProps) => {
    const { formatText } = useLocalization();
    const [isMounted, setIsMounted] = React.useState(false);
    const { flexTemplateNode, flexModuleModelStore, context, rateCalendar, flexViewModel, hotels: hotelsStore } = props;
    const { id, options } = flexTemplateNode;
    const model = flexModuleModelStore.getModel(id) as HotelsFlexModuleResult | null;
    let hotelsOnMap: boolean;
    let locationId: string;

    React.useEffect(() => {
      setIsMounted(true);
      if (hotelsStore && hotels && hotels.length > 0 && hotelsOnMap) {
        hotelsStore.fetchMapHotelsById(hotels, context, locationId);
      }
    }, []);

    const lodgingRebound = React.useMemo(() => {
      if (model && model.showLodgingRebound && isMounted) {
        return <LodgingRebound inputs={{ regionId: locationId }} />;
      }

      return null;
    }, [isMounted]);

    if (!model) {
      return null;
    }

    const useFilteredHotels = id === "hotels-1" && hotelsStore?.filteredHotels?.length;
    const hotels: Hotel[] = useFilteredHotels ? hotelsStore!.filteredHotels : model.hotels;

    if (!hotels || hotels.length === 0 || !hotelsStore) {
      return null;
    }
    if (rateCalendar && flexViewModel) {
      rateCalendar.setup(flexViewModel.flexContext, context, hotels);
    }

    const affinityDescription = context?.searchContext?.affinity?.description;
    const affinityTitle = model.view === "affinity" ? "affinityTitle" : "";
    locationId = model.destinationId ?? context?.searchContext?.location?.id;

    hotelsOnMap = typeof model.showMarkersOnMap === "undefined" || model.showMarkersOnMap;
    const isAffinityFiltersEnabled = isVariantEnabled(context, Experiment.Flex_Blossom_Affinity_Pool_Filters);
    const showSeeAllButton = isAffinityFiltersEnabled && hotels.length === 1;
    const { showSuggestedFilters, showMoreStrategy, showLoyaltySignUp, showPriceTrendGraphAnchor } = model;
    const addTopPaddingLegalText = !showSuggestedFilters && showMoreStrategy !== "button" && hotels.length > 1;

    const isLoading = hotelsStore.isLoadingFilterHotels;
    const overfilteredMessage = hotelsStore.overfilteredMessage;

    if (!isLoading && !overfilteredMessage && props.hotels?.filteredHotels?.length) {
      liveAnnounce(formatText("propertyfilters.results.showing.news"));
    }

    return (
      <HotelsContext.Provider value={{ context, flexTemplateNode, model }}>
        <SchemaOrgScript structuredData={hotelStructuredData(hotels)} />
        <div
          className={`Hotels all-cell-1-1 uitk-cell all-b-padding-three ${
            model.showRedPrices ? "hotelPricingApac" : ""
          }`}
          id={id}
          data-fm={options.fmId}
          data-fm-title-id={options.fmTitleId}
        >
          {model.title && (
            <h2 className={`uitk-type-heading-600 all-t-padding-three ${affinityTitle}`}>{model.title}</h2>
          )}
          {model.editorialContent && <UitkSubheading>{model.editorialContent}</UitkSubheading>}
          {lodgingRebound && <div className="all-t-margin-three">{lodgingRebound}</div>}
          <Viewport>
            <ViewMedium>
              <>
                {isLoading && <PlaceholderResults />}
                {!isLoading && overfilteredMessage && <MessagingCard {...overfilteredMessage} />}
                {!isLoading &&
                  !overfilteredMessage &&
                  hotels.map((hotel, index) => (
                    <Result
                      key={hotel.hotelId}
                      hotel={hotel}
                      index={index}
                      isMobile={false}
                      locationId={locationId}
                      context={context}
                      model={model}
                      cardContent={renderCardContent}
                      isAffinityFiltersEnabled={isAffinityFiltersEnabled}
                      hasLoyaltySignUp={showLoyaltySignUp}
                      hasPriceTrendGraph={showPriceTrendGraphAnchor}
                    />
                  ))}
              </>
            </ViewMedium>
            <ViewSmall>
              <>
                <SeeAllContentLink model={model} context={context} affinityDescription={affinityDescription} />
                {isLoading && <PlaceholderResults />}
                {!isLoading && overfilteredMessage && <MessagingCard {...overfilteredMessage} />}
                {!isLoading &&
                  !overfilteredMessage &&
                  hotels.map((hotel, index) => (
                    <Result
                      key={hotel.hotelId}
                      hotel={hotel}
                      index={index}
                      isMobile={true}
                      locationId={locationId}
                      context={context}
                      model={model}
                      cardContent={renderCardContent}
                      isAffinityFiltersEnabled={isAffinityFiltersEnabled}
                      hasLoyaltySignUp={showLoyaltySignUp}
                      hasPriceTrendGraph={showPriceTrendGraphAnchor}
                    />
                  ))}
              </>
            </ViewSmall>
          </Viewport>
          {showSeeAllButton ? null : <SeeAllButton />}
          <HotelStarRatingAffinityFilters />
          <LegalText className={addTopPaddingLegalText ? "all-t-padding-three" : ""} />
        </div>
      </HotelsContext.Provider>
    );
  })
);

export const Hotels = observer(HotelsComponent);
export default Hotels;

interface ResultProps {
  hotel: Hotel;
  index: number;
  isMobile: boolean;
  locationId: string;
  context: ExtendedContextStore;
  model: HotelsFlexModuleResult;
  cardContent: CardContentFunc;
  isAffinityFiltersEnabled?: boolean;
  hasLoyaltySignUp: boolean;
  hasPriceTrendGraph?: boolean;
}

const Result: React.FC<ResultProps> = ({
  hotel,
  index,
  isMobile,
  locationId,
  context,
  model,
  cardContent,
  isAffinityFiltersEnabled,
  hasLoyaltySignUp,
  hasPriceTrendGraph,
}) => {
  const isAffinityView = model.view === "affinity";

  const numberOfCardsToInitiallyShow = isMobile ? 2 : 3;
  const Wrapper = index < numberOfCardsToInitiallyShow ? React.Fragment : LazyLoad;
  const wrapperProps = index < numberOfCardsToInitiallyShow ? null : { context };

  const hotelCard = (
    <HotelCard
      hotel={hotel}
      locationId={locationId}
      index={index + 1}
      callToActionHotelName={model.callToActionHotelName}
      context={context}
      CardContent={cardContent(hotel, isMobile, model, context)}
      isMobile={isMobile}
      showGuestRating={model.showGuestRating}
      displayBadges={true}
      showHeartButton={model.showHeartButton}
      withBorder={isAffinityView}
    />
  );

  const loyaltyAuth = hasLoyaltySignUp && index === 3 && (
    <LoyaltySignup classNames="all-t-margin-three" context={context} />
  );
  const priceTrendAnchor = hasPriceTrendGraph && index === 6 && (
    <PriceTrendGraphAnchor
      location={context.searchContext.location.localizedName}
      locationType={context.searchContext.location.type}
    />
  );
  const affinityFilters = (index === 0 || model.hotels.length === 1) && isAffinityFiltersEnabled && (
    <HotelPoolAffinityFilters />
  );

  if (model.alternativeHotelsOptions && model.alternativeHotelsOptions.position === index) {
    return (
      <Wrapper {...wrapperProps}>
        <>
          <HotelInterruption {...model.alternativeHotelsOptions} isMobile={isMobile} />
          {loyaltyAuth}
          {priceTrendAnchor}
          {hotelCard}
          {affinityFilters}
        </>
      </Wrapper>
    );
  }

  return (
    <Wrapper {...wrapperProps}>
      <>
        {loyaltyAuth}
        {priceTrendAnchor}
        {hotelCard}
        {affinityFilters}
      </>
    </Wrapper>
  );
};

type CardContentFunc = (
  hotel: Hotel,
  isMobile: boolean,
  model: HotelsFlexModuleResult,
  context: ExtendedContextStore
) => () => JSX.Element;

const renderCardContent: CardContentFunc = (hotel, isMobile, model, context) => () => (
  <UitkCardAlohaContentSection>
    <CardContentReviewsPriceProposition
      hotel={hotel}
      locale={context.locale}
      locationName={model.locationName}
      context={context}
      showDistance={model.showDistance}
      showAddress={model.showAddress}
      shouldUseImperialDistanceUnits={model.shouldUseImperialDistanceUnits}
      addressDistanceView={model.addressDistanceView}
    />
  </UitkCardAlohaContentSection>
);
